import { useMemo } from 'react'
import type { PayloadFormat } from '@/lib/mqtt-types'

export interface PayloadAnalysis {
  format: PayloadFormat
  formatted: string
  raw: string
  size: number
  isValid: boolean
  error?: string
}

export function usePayloadDetection(payload: string): PayloadAnalysis {
  return useMemo(() => {
    const raw = payload
    const size = new Blob([payload]).size

    // Detect format and format payload
    const trimmed = payload.trim()

    // Try JSON
    try {
      const parsed = JSON.parse(trimmed)
      return {
        format: 'json',
        formatted: JSON.stringify(parsed, null, 2),
        raw,
        size,
        isValid: true,
      }
    } catch {
      // Not JSON
    }

    // Try XML
    if (trimmed.startsWith('<') && trimmed.endsWith('>')) {
      const formatted = formatXml(trimmed)
      return {
        format: 'xml',
        formatted,
        raw,
        size,
        isValid: true,
      }
    }

    // Try Hex
    if (/^[0-9a-fA-F]+$/.test(trimmed) && trimmed.length % 2 === 0 && trimmed.length > 0) {
      const formatted = formatHex(trimmed)
      return {
        format: 'hex',
        formatted,
        raw,
        size,
        isValid: true,
      }
    }

    // Try Base64
    if (/^[A-Za-z0-9+/]+=*$/.test(trimmed) && trimmed.length % 4 === 0 && trimmed.length > 0) {
      try {
        const decoded = atob(trimmed)
        const reencoded = btoa(decoded)
        if (reencoded === trimmed) {
          // It's valid Base64, try to decode as string
          const decodedStr = tryDecodeBase64(trimmed)
          return {
            format: 'base64',
            formatted: `Decoded:\n${decodedStr}\n\nRaw Base64:\n${trimmed}`,
            raw,
            size,
            isValid: true,
          }
        }
      } catch {
        // Not valid Base64
      }
    }

    // Default to plain text
    return {
      format: 'text',
      formatted: trimmed,
      raw,
      size,
      isValid: true,
    }
  }, [payload])
}

// Helper function to format XML with indentation
function formatXml(xml: string): string {
  try {
    let formatted = ''
    let indent = ''
    const tab = '  '

    xml.split(/>\s*</).forEach((node, index) => {
      if (index > 0) {
        // Check if this is a closing tag
        if (node.match(/^\/\w/)) {
          indent = indent.substring(tab.length)
        }
        formatted += '\n' + indent
      }

      formatted +=
        (index > 0 ? '<' : '') + node + (index < xml.split(/>\s*</).length - 1 ? '>' : '')

      // Check if this is an opening tag (not self-closing)
      if (node.match(/^<?\w[^>]*[^/]$/)) {
        indent += tab
      }
    })

    return formatted
  } catch {
    return xml
  }
}

// Helper function to format hex with spacing
function formatHex(hex: string): string {
  const pairs = hex.match(/.{1,2}/g) || []
  const rows: string[] = []

  for (let i = 0; i < pairs.length; i += 16) {
    const rowPairs = pairs.slice(i, i + 16)
    const hexPart = rowPairs.join(' ')
    const asciiPart = rowPairs
      .map((byte) => {
        const charCode = parseInt(byte, 16)
        return charCode >= 32 && charCode <= 126 ? String.fromCharCode(charCode) : '.'
      })
      .join('')

    const offset = i.toString(16).padStart(4, '0').toUpperCase()
    rows.push(`${offset}  ${hexPart.padEnd(47)}  ${asciiPart}`)
  }

  return rows.join('\n')
}

// Helper function to decode Base64 safely
function tryDecodeBase64(base64: string): string {
  try {
    const decoded = atob(base64)
    // Check if the decoded content is printable
    const isPrintable = [...decoded].every((char) => {
      const code = char.charCodeAt(0)
      return (code >= 32 && code <= 126) || code === 9 || code === 10 || code === 13
    })

    if (isPrintable) {
      return decoded
    }

    // Return hex representation for binary data
    return `[Binary data - ${decoded.length} bytes]`
  } catch {
    return '[Failed to decode]'
  }
}

// Standalone function for payload format detection
export function detectPayloadFormat(payload: string): PayloadFormat {
  const trimmed = payload.trim()

  // Try JSON
  try {
    JSON.parse(trimmed)
    return 'json'
  } catch {
    // Not JSON
  }

  // Try XML
  if (trimmed.startsWith('<') && trimmed.endsWith('>')) {
    return 'xml'
  }

  // Try Hex
  if (/^[0-9a-fA-F]+$/.test(trimmed) && trimmed.length % 2 === 0) {
    return 'hex'
  }

  // Try Base64
  if (/^[A-Za-z0-9+/]+=*$/.test(trimmed) && trimmed.length % 4 === 0) {
    try {
      const decoded = atob(trimmed)
      const reencoded = btoa(decoded)
      if (reencoded === trimmed) {
        return 'base64'
      }
    } catch {
      // Not valid Base64
    }
  }

  return 'text'
}

// Format payload based on detected or specified format
export function formatPayload(payload: string, format: PayloadFormat): string {
  const trimmed = payload.trim()

  switch (format) {
    case 'json':
      try {
        return JSON.stringify(JSON.parse(trimmed), null, 2)
      } catch {
        return trimmed
      }

    case 'xml':
      return formatXml(trimmed)

    case 'hex':
      return formatHex(trimmed)

    case 'base64':
      return tryDecodeBase64(trimmed)

    default:
      return trimmed
  }
}

export default usePayloadDetection
